import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/site/AvatarUpload";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/site/NotificationBell";
import { InstallButton } from "@/components/site/InstallButton";

const linkCls = ({ isActive }) =>
  `text-sm font-medium whitespace-nowrap transition-colors hover:text-[#1A9AA8] ${isActive ? "text-[#1A9AA8]" : "text-[#0B2A4A]"}`;

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const links = [
    { to: "/", label: "Home", id: "nav-home-link" },
    { to: "/digital-nomad-beginners-course", label: "Beginners Course", id: "nav-course-link" },
    { to: "/retreats", label: "Retreats", id: "nav-retreats-link" },
    { to: "/ai-website-developer-course", label: "AI Website Course", id: "nav-ai-course-link" },
    { to: "/online-courses", label: "Online Courses", id: "nav-online-courses-link" },
    { to: "/jobs", label: "Jobs", id: "nav-jobs-link" },
    { to: "/community", label: "Community", id: "nav-community-link" },
    { to: "/nicaragua", label: "Nicaragua", id: "nav-nicaragua-link", more: true },
    { to: "/greg-miceli", label: "Greg Miceli", id: "nav-greg-link", more: true },
    { to: "/about-the-host", label: "About the Host", id: "nav-about-link", more: true },
    { to: "/blog", label: "Blog", id: "nav-blog-link", more: true },
    { to: "/graduates", label: "Graduates", id: "nav-graduates-link", more: true },
    { to: "/contact", label: "Contact", id: "nav-contact-link", more: true },
    ...(user && user.role === "host" ? [{ to: "/host", label: "Host Dashboard", id: "nav-host-link" }] : []),
    ...(user && user.role !== "host" ? [{ to: "/dashboard", label: "My Course", id: "nav-dashboard-link" }] : []),
  ];
  const doLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };
  return (
    <header className="site-header sticky top-0 z-50 backdrop-blur-xl bg-[#FAF6EE]/85 border-b border-[#0B2A4A]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
          <img src="/brand/logo-horizontal.jpg" alt="Digital Nomad Nicaragua logo" className="h-10 sm:h-11 w-auto mix-blend-multiply" />
        </Link>
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5 min-w-0" aria-label="Main">
          {links.filter((l) => !l.more).map((l) => (
            <NavLink key={l.to} to={l.to} end className={linkCls} data-testid={l.id}>{l.label}</NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 text-sm font-medium text-[#0B2A4A] hover:text-[#1A9AA8] outline-none" data-testid="nav-more-btn">More <ChevronDown className="h-4 w-4" /></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" data-testid="nav-more-menu">
              {links.filter((l) => l.more).map((l) => (
                <DropdownMenuItem key={l.to} asChild><Link to={l.to} className="cursor-pointer" data-testid={l.id}>{l.label}</Link></DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <InstallButton />
          {user && <NotificationBell />}
          {user && <Avatar user={user} className="h-8 w-8" testId="header-avatar" />}
          {user ? (
            <button onClick={doLogout} className="hidden xl:inline-flex items-center gap-1.5 text-sm font-medium text-[#0B2A4A] hover:text-[#F08A3C] px-3 py-2" data-testid="nav-sign-out-btn">
              <LogOut className="h-4 w-4" /> <span className="hidden 2xl:inline">Sign out</span>
            </button>
          ) : (
            <Link to="/login" className="hidden xl:inline-flex rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-4 py-2 text-sm font-semibold hover:bg-[#071C33] transition-colors" data-testid="nav-sign-in-btn">
              Sign in
            </Link>
          )}
          <button className="xl:hidden p-2 text-[#0B2A4A]" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-menu-btn">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-[#0B2A4A]/10 bg-[#FAF6EE] px-4 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end className={linkCls} onClick={() => setOpen(false)} data-testid={`${l.id}-mobile`}>{l.label}</NavLink>
          ))}
          {user ? (
            <button onClick={doLogout} className="text-left text-sm font-medium text-[#0B2A4A]" data-testid="nav-sign-out-btn-mobile">Sign out</button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#F08A3C]" data-testid="nav-sign-in-btn-mobile">Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
